import React from 'react';
import { Edit, X } from 'lucide-react';
import { Badge, Select, IconButton } from '@anyemp/ui-kit';
import { getStatusStyles } from '../../utils/colorUtils';
import { getTalentTypeLabel } from '../../utils/talentTypeUtils';

/**
 * StageSelect Component
 *
 * Отдельная ось от StatusSelect: стадия отвечает «где талант находится»,
 * статус — «что с ним на этой стадии». Ровно то же read-only/edit поведение,
 * что и у StatusSelect (тот же бейдж, тот же inline-редактор), но со своим
 * набором опций — стадиями, а не статусами.
 *
 * @param {Object} props
 * Стадия задаёт тип таланта, поэтому стадии другого типа в списке помечены
 * этим типом: их выбор сменит тип таланта (причину спросит модалка перехода).
 *
 * @param {Object|null} props.currentStage - Текущая стадия { id, name, color, talent_type }
 * @param {Array} props.stageOptions - Доступные стадии, отсортированные по stage_order
 * @param {boolean} props.isEditing
 * @param {boolean} props.isLoading
 * @param {Function} props.onEdit
 * @param {Function} props.onCancel
 * @param {Function} props.onChange - вызывается с stageId
 * @param {string} props.emptyLabel
 * @param {string} props.selectPlaceholder
 */
const StageSelect = ({
  currentStage,
  stageOptions = [],
  isEditing = false,
  isLoading = false,
  onEdit,
  onCancel,
  onChange,
  emptyLabel = "Not defined",
  selectPlaceholder = "Select stage"
}) => {
  if (!isEditing) {
    return (
      <div className="flex items-center space-x-2">
        {currentStage ? (
          <Badge
            label={currentStage.name}
            variant="subtle"
            size="small"
            style={getStatusStyles(currentStage.color, {
              backgroundColorOpacity: 0.15,
              borderOpacity: 0.4,
              textOpacity: 1
            })}
          />
        ) : (
          <Badge label={emptyLabel} variant="subtle" size="small" color="neutral" />
        )}
        {onEdit && (
          <IconButton
            variant="ghost"
            color="neutral"
            size="small"
            aria-label="Edit stage"
            title="Edit stage"
            onClick={onEdit}
            icon={<Edit className="h-3 w-3" />}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 items-center gap-2 sm:min-w-[180px]">
      <Select
        value={currentStage?.id ? String(currentStage.id) : null}
        onChange={(v) => onChange(v)}
        disabled={isLoading}
        size="small"
        fullWidth
        placeholder={selectPlaceholder}
        options={stageOptions.map((stage) => ({
          value: String(stage.id),
          label: currentStage?.talent_type && stage.talent_type && stage.talent_type !== currentStage.talent_type
            ? `${stage.name} · ${getTalentTypeLabel(stage.talent_type)}`
            : stage.name
        }))}
      />
      {onCancel && (
        <IconButton
          variant="ghost"
          color="error"
          size="small"
          aria-label="Cancel"
          title="Cancel"
          disabled={isLoading}
          onClick={onCancel}
          icon={<X className="h-4 w-4" />}
        />
      )}
    </div>
  );
};

export default StageSelect;
